import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

/** A user's color scheme preference. `system` defers to the OS setting. */
export type ColorScheme = "light" | "dark" | "system";

/**
 * localStorage key holding the saved {@link ColorScheme}. Read synchronously by
 * the boot script in `index.html` before first paint to avoid a flash of the
 * wrong theme, so the key must stay in sync between the two.
 */
export const COLOR_SCHEME_STORAGE_KEY = "color-scheme";

/** The `userData` (type `config`) name under which the preference is persisted. */
export const COLOR_SCHEME_USER_DATA_NAME = "color_scheme";

const COLOR_SCHEMES: ColorScheme[] = ["light", "dark", "system"];

/** Narrow an arbitrary string to a {@link ColorScheme}, or `null` if invalid. */
export function parseColorScheme(
  value: string | null | undefined,
): ColorScheme | null {
  return COLOR_SCHEMES.includes(value as ColorScheme)
    ? (value as ColorScheme)
    : null;
}

const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

function readStoredColorScheme(): ColorScheme {
  try {
    return (
      parseColorScheme(localStorage.getItem(COLOR_SCHEME_STORAGE_KEY)) ??
      "system"
    );
  } catch {
    // localStorage may be unavailable (e.g. privacy mode).
    return "system";
  }
}

/** The `prefers-color-scheme: dark` query, or `null` where unsupported. */
function darkModeQuery(): MediaQueryList | null {
  return typeof window.matchMedia === "function"
    ? window.matchMedia(DARK_MEDIA_QUERY)
    : null;
}

/** Resolve a preference to the concrete theme applied to the DOM. */
function resolveTheme(scheme: ColorScheme): "light" | "dark" {
  if (scheme === "system") {
    return darkModeQuery()?.matches ? "dark" : "light";
  }
  return scheme;
}

function applyTheme(scheme: ColorScheme) {
  document.documentElement.setAttribute("data-theme", resolveTheme(scheme));
}

interface ThemeContextValue {
  /** The user's saved preference. */
  colorScheme: ColorScheme;
  /** Update the preference: applies it immediately and caches it to localStorage. */
  setColorScheme(scheme: ColorScheme): void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Props for the ThemeProvider component */
interface Props {
  /** Subtree that can read and update the color scheme */
  children: React.ReactNode;
}

/**
 * Owns the color scheme preference for the running app. The DOM is already
 * themed by the boot script in `index.html`; this provider takes over runtime
 * changes — re-applying the `data-theme` attribute and, while in `system` mode,
 * following the OS `prefers-color-scheme` setting.
 */
export function ThemeProvider({ children }: Props) {
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(
    readStoredColorScheme,
  );

  useEffect(() => {
    applyTheme(colorScheme);

    if (colorScheme !== "system") {
      return;
    }

    const mql = darkModeQuery();
    if (!mql) {
      return;
    }
    const onChange = () => applyTheme("system");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [colorScheme]);

  const setColorScheme = useCallback((scheme: ColorScheme) => {
    try {
      localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, scheme);
    } catch {
      // localStorage may be unavailable (e.g. privacy mode); preference still
      // applies for this session via state below.
    }
    setColorSchemeState(scheme);
  }, []);

  return (
    <ThemeContext.Provider value={{ colorScheme, setColorScheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
